export interface FamilyPaymentRow {
  _id: string;
  studentId?: string | null;
  studentName?: string | null;
  className?: string | null;
  status: string;
  finalAmount?: number | null;
  dueDate?: string | null;
  paymentDate?: string | null;
  paymentMethod?: string | null;
  internalTransactionId?: string | null;
  metadata?: {
    paymentIntentId?: string | null;
    invoiceGeneratedBy?: string | null;
    walletAmountUsed?: number | null;
  } | null;
  payments?: Array<{ paymentIntentId?: string | null; amount?: number | null }>;
}

const OPEN_STATUSES = new Set([
  'failed',
  'partially paid',
  'pending',
  'scheduled',
]);
const UPCOMING_STATUSES = new Set(['upcoming']);
const DUE_SENSITIVE_STATUSES = new Set(['invoice scheduled']);
const RECENT_STATUSES = new Set([
  'paid',
  'failed',
  'partially paid',
  'full refund',
  'partial refund',
  'free',
]);

function isDueOnOrBeforeToday(dueDate: string | null | undefined): boolean {
  if (!dueDate) return true;
  const dueDay = dueDate.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dueDay)) return true;
  const today = new Date();
  const todayDay = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0'),
  ].join('-');
  return dueDay <= todayDay;
}

export function familyPaymentCaptureKey(row: FamilyPaymentRow): string {
  const fromPayments = row.payments?.find((payment) => payment.paymentIntentId)
    ?.paymentIntentId;
  return (
    row.metadata?.paymentIntentId ||
    fromPayments ||
    row.internalTransactionId ||
    row._id
  );
}

export interface FamilyPaymentLine {
  id: string;
  studentId: string;
  studentName: string;
  className: string;
  chargeLabel: string;
  status: string;
  amount: number;
  dueDate: string | null;
}

export interface FamilyPaymentSummary {
  open: FamilyPaymentLine[];
  upcoming: FamilyPaymentLine[];
  recent: FamilyPaymentLine[];
  nextDueDate: string | null;
}

function money(value: number | null | undefined): number {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount) ? amount : 0;
}

export function familyPaymentChargeLabel(row: FamilyPaymentRow): string {
  const className = row.className?.trim();
  if (className) return className;

  const generatedBy = row.metadata?.invoiceGeneratedBy?.trim().toLowerCase();
  if (generatedBy?.includes('manual')) return 'Manual charge';
  if (generatedBy?.includes('wallet')) return 'Wallet add';
  if ((row.metadata?.walletAmountUsed ?? 0) > 0) return 'Wallet charge';

  const method = row.paymentMethod?.trim().toLowerCase();
  if (method === 'wallet') return 'Wallet charge';

  return 'Charge';
}

function toLine(
  row: FamilyPaymentRow,
  amount = money(row.finalAmount),
): FamilyPaymentLine {
  const className = row.className?.trim() || '';
  return {
    id: row._id,
    studentId: row.studentId ?? '',
    className,
    chargeLabel: familyPaymentChargeLabel(row),
    studentName: row.studentName?.trim() || 'Student',
    status: row.status,
    amount,
    dueDate: row.dueDate ?? null,
  };
}

export function summarizeFamilyPayments(
  rows: FamilyPaymentRow[],
): FamilyPaymentSummary {
  const open: FamilyPaymentRow[] = [];
  const upcoming: FamilyPaymentRow[] = [];

  for (const row of rows) {
    const status = row.status?.trim().toLowerCase() ?? '';
    if (OPEN_STATUSES.has(status)) {
      open.push(row);
      continue;
    }
    if (DUE_SENSITIVE_STATUSES.has(status)) {
      if (isDueOnOrBeforeToday(row.dueDate)) {
        open.push(row);
      } else {
        upcoming.push(row);
      }
      continue;
    }
    if (UPCOMING_STATUSES.has(status)) {
      upcoming.push(row);
    }
  }

  const nextDueDate =
    upcoming
      .map((row) => row.dueDate)
      .filter((date): date is string => Boolean(date))
      .sort()[0] ?? null;

  const recentByCapture = new Map<string, FamilyPaymentRow>();
  for (const row of rows) {
    const status = row.status?.trim().toLowerCase() ?? '';
    if (!RECENT_STATUSES.has(status)) continue;
    const key = familyPaymentCaptureKey(row);
    const current = recentByCapture.get(key);
    const rowTime = row.paymentDate || row.dueDate || '';
    const currentTime = current?.paymentDate || current?.dueDate || '';
    if (!current || rowTime > currentTime) {
      recentByCapture.set(key, row);
    }
  }

  const recent = [...recentByCapture.values()]
    .sort((a, b) =>
      (b.paymentDate || b.dueDate || '').localeCompare(
        a.paymentDate || a.dueDate || '',
      ),
    )
    .slice(0, 5)
    .map((row) => {
      const siblings = rows.filter(
        (candidate) =>
          familyPaymentCaptureKey(candidate) === familyPaymentCaptureKey(row),
      );
      const names = [
        ...new Set(
          siblings.map((sibling) => sibling.studentName?.trim() || 'Student'),
        ),
      ];
      const chargeLabels = [
        ...new Set(siblings.map((sibling) => familyPaymentChargeLabel(sibling))),
      ];
      const amount = siblings.every(
        (sibling) => money(sibling.finalAmount) === money(row.finalAmount),
      )
        ? money(row.finalAmount)
        : siblings.reduce((sum, sibling) => sum + money(sibling.finalAmount), 0);
      const line = toLine(row, amount);
      return {
        ...line,
        studentName: names.join(', '),
        chargeLabel: chargeLabels.join(', '),
        className: chargeLabels.join(', '),
      };
    });

  const byDueDate = (a: FamilyPaymentLine, b: FamilyPaymentLine) =>
    (a.dueDate || '').localeCompare(b.dueDate || '');

  return {
    open: open.map((row) => toLine(row)).sort(byDueDate),
    upcoming: upcoming.map((row) => toLine(row)).sort(byDueDate),
    recent,
    nextDueDate,
  };
}

export function formatSavedPaymentMethod(method: {
  type?: string;
  brand?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
  bankName?: string;
} | null): string {
  if (!method) return 'No saved payment method';
  const brand = method.brand || method.bankName || method.type || 'Payment method';
  const last4 = method.last4 ? ` •••• ${method.last4}` : '';
  const expiry =
    method.exp_month && method.exp_year
      ? ` exp ${method.exp_month}/${method.exp_year}`
      : '';
  return `${brand}${last4}${expiry}`;
}
